import {
  FC,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
  memo,
} from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { APP_ROUTES } from '@navigation/types';
import { useModal } from '@hooks/useModal';
import { useAgoraEngine } from '@providers/AgoraEngineProvider';
import { useRecordedShow } from '@providers/RecordedShowProvider';
import { useShowDataById } from '@redux/selectors/shows';
import { getListenersText, formatDuration } from '@utils/index';
import Modal from '@components/modal';
import { PremierText } from '@components/text';
import Image from '@components/image';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import cardImage from '@assets/images/card.png';

import * as Styled from './styled';
import SkewedLabel from './components/SkewedLabel';

const CARD_WIDTH = 200;
const CARD_HEIGHT = 100;

const Container = styled(TouchableOpacity)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[800]};
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 12px;
  }
`;

export const ImageContainer = styled.div`
  position: relative;
  width: ${CARD_WIDTH}px;
  height: ${CARD_HEIGHT}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;

export const ShowImage = styled(Image)`
  position: absolute;
  border: 0.5px solid ${({ theme }) => theme.colors.grey[600]};
`;

const MainInfoContainer = styled.div`
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
`;

const TitleText = styled(Styled.TitleText)`
  -webkit-line-clamp: 2;
  text-align: left;
  line-height: 1.4;
  word-break: break-word;
  margin-bottom: 4px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.md};
  }
`;

const InfoContainer = styled(Styled.InfoContainer)`
  flex-wrap: wrap;
  gap: 4px;
`;

type Props = {
  showId: string;
};

const RecordedShowCard: FC<Props> = ({ showId }) => {
  const { title, listenersCount, image, createdDate, recordDuration } =
    useShowDataById(showId);
  const [showImage, setShowImage] = useState<string>(image || ' ');
  const duration = useMemo(
    () => recordDuration && formatDuration(Number(recordDuration)),
    [recordDuration],
  );
  const { currentShowId, onFinishOrLeaveCurrentShow } = useAgoraEngine();
  const { currentRecordedShowId, closeCurrentRecordedShow } = useRecordedShow();
  const navigate = useNavigate();
  const { isModalShown, showModal, hideModal } = useModal();

  const onImageLoad = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    if (e.currentTarget.naturalWidth === 0) {
      setShowImage(cardImage);
    }
  }, []);

  const onImageError = useCallback(() => {
    setShowImage(cardImage);
  }, []);

  const goToShow = useCallback(() => {
    if (
      (!!currentShowId && currentShowId !== showId) ||
      (!!currentRecordedShowId && currentRecordedShowId !== showId)
    ) {
      showModal();
      return;
    }

    navigate(APP_ROUTES.SHOWS_INITIALIZATION, { state: { id: showId } });
  }, [showId, currentShowId, currentRecordedShowId, navigate, showModal]);

  const onConfirm = useCallback(() => {
    if (currentShowId) {
      onFinishOrLeaveCurrentShow();
    }

    if (currentRecordedShowId) {
      closeCurrentRecordedShow();
    }
    hideModal();
  }, [
    hideModal,
    currentShowId,
    currentRecordedShowId,
    onFinishOrLeaveCurrentShow,
    closeCurrentRecordedShow,
  ]);

  return (
    <Container onClick={goToShow}>
      <ImageContainer>
        <ShowImage
          src={showImage}
          onImageLoad={onImageLoad}
          onImageError={onImageError}
          alt='Image'
        />

        <Styled.TopImageContainerPart />

        <Styled.BottomImageContainerPart>
          <div />

          {duration ? <SkewedLabel key={showId}>{duration}</SkewedLabel> : null}
        </Styled.BottomImageContainerPart>
      </ImageContainer>

      <MainInfoContainer>
        <TitleText>{title}</TitleText>

        <InfoContainer>
          <Styled.SubTitleText>
            {getListenersText(listenersCount)}
          </Styled.SubTitleText>

          {!!createdDate && (
            <>
              <Styled.Ellipse />

              <Styled.SubTitleText>
                {`${createdDate ? formatDistanceToNow(createdDate) : ''} ago`}
              </Styled.SubTitleText>
            </>
          )}
        </InfoContainer>
      </MainInfoContainer>

      <Modal
        isShown={isModalShown}
        onClose={hideModal}
        onConfirm={onConfirm}
        isCloseable
      >
        <PremierText>Are you sure you want to exit current show?</PremierText>
      </Modal>
    </Container>
  );
};

export default memo(RecordedShowCard);
